import React, { useCallback, useEffect, useRef, useState } from 'react';
import redditApi from '../../../services/redditApi';

const POLL_MS = 3000;
const TERMINAL = ['passed', 'failed', 'needs_manual_review', 'error'];

const STATUS_LABEL = {
  checking: 'Checking',
  passed: 'Passed',
  failed: 'Failed',
  needs_manual_review: 'Needs Manual Review',
  error: 'Error',
};

const CHECK_ICON = { passed: '\u2713', failed: '\u2717', needs_manual_review: '!', error: '\u2717' };

function formatCheckKey(key) {
  return (key || '').replace(/_/g, ' ');
}

function VisibilityActionCallout({ actionRequired }) {
  if (!actionRequired || actionRequired.code !== 'enable_public_profile') return null;

  const passedLabels = (actionRequired.passed_checks || []).map(formatCheckKey);
  const title = actionRequired.is_retry
    ? 'Public activity still not visible'
    : 'Almost there — enable public profile visibility';

  return (
    <div className={`rh-qg-callout${actionRequired.is_retry ? ' rh-qg-callout-retry' : ''}`}>
      <div className="rh-qg-callout-title">{title}</div>
      {passedLabels.length > 0 && (
        <p className="rh-qg-callout-passed">
          These checks passed: {passedLabels.join(', ')}.
        </p>
      )}
      <p className="rh-qg-callout-body">
        {actionRequired.is_retry
          ? 'We still cannot inspect your public comments or posts. If you enabled profile visibility, this may indicate deleted history or an account not in good standing.'
          : 'Your account looks good on profile metrics, but we need to briefly inspect your public comments and posts before approval.'}
      </p>
      {actionRequired.instructions && (
        <p className="rh-qg-callout-instructions">{actionRequired.instructions}</p>
      )}
    </div>
  );
}

/**
 * Drives the validate -> poll lifecycle for one Reddit username.
 *
 * Returns { phase, result, error, validate, reset }.
 *   phase: 'idle' | 'checking' | 'done'
 *   result: the latest validation payload (or null)
 */
export function useAccountQualityCheck() {
  const [phase, setPhase] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    setPhase('idle');
    setResult(null);
    setError('');
  }, [stopPolling]);

  const poll = useCallback((validationId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await redditApi.get(`/accounts/validations/${validationId}/`);
        setResult(data);
        if (TERMINAL.includes(data.status)) {
          stopPolling();
          setPhase('done');
        }
      } catch {
        // transient network blip — keep polling
      }
    }, POLL_MS);
  }, [stopPolling]);

  const validate = useCallback(async (rawUsername) => {
    const username = (rawUsername || '').trim().replace(/^u\//i, '');
    if (!username) return;
    setError('');
    setResult(null);
    setPhase('checking');
    try {
      const { data } = await redditApi.post('/accounts/validate/', { username });
      setResult({ validation_id: data.validation_id, username: data.username, status: 'checking' });
      poll(data.validation_id);
    } catch (err) {
      const status = err.response?.status;
      setPhase('idle');
      setError(
        status === 429
          ? 'Too many attempts. Please wait a moment and try again.'
          : err.response?.data?.detail || 'Could not start validation. Please try again.',
      );
    }
  }, [poll]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  return { phase, result, error, validate, reset };
}

function StatusBadge({ status }) {
  return (
    <span className={`rh-badge rh-badge-${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function formatLatest(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return '—';
  }
}

/**
 * Renders an account's niches as small pill tags. Reused by the Add Account
 * quality-check result and the Reddit Accounts table.
 *
 * Props:
 *   niches         — string[]
 *   max            — cap the number of tags shown inline; remainder collapse into a "+N" pill
 *   preferShortest — when max is set, show the shortest tags first (keeps table rows compact)
 *   nowrap         — keep tags on a single line (no wrap)
 *   emptyText      — text shown when there are no niches (omit to render nothing)
 */
export function NicheTags({ niches, max, preferShortest = false, nowrap = false, emptyText }) {
  const list = Array.isArray(niches) ? niches.filter(Boolean) : [];
  if (list.length === 0) {
    return emptyText ? <span className="rh-niche-empty">{emptyText}</span> : null;
  }
  const ordered = preferShortest && max
    ? [...list].sort((a, b) => a.length - b.length || a.localeCompare(b))
    : list;
  const shown = max ? ordered.slice(0, max) : ordered;
  const hidden = max ? ordered.slice(max) : [];
  const className = nowrap
    ? 'rh-qg-niche-tags rh-qg-niche-tags--compact'
    : 'rh-qg-niche-tags';
  return (
    <span className={className} title={list.join(', ')}>
      {shown.map((tag) => (
        <span className="rh-niche-tag" key={tag}>{tag}</span>
      ))}
      {hidden.length > 0 && (
        <span className="rh-niche-tag rh-niche-tag-more" title={hidden.join(', ')}>
          +{hidden.length}
        </span>
      )}
    </span>
  );
}

/**
 * Renders the quality-check result: status badge, metrics, per-check list,
 * AI assessment, rejection reasons, and the Add Account / Retry actions.
 *
 * Props:
 *   result       — validation payload
 *   adding       — bool, save in progress
 *   onAddAccount — () => void, called when "Add Account" is clicked (status passed)
 *   onRetry      — () => void, called to reset / check another username
 *   onRerun      — () => void, re-run check for the same username (visibility action)
 *   addError     — optional error string from the save step
 */
export function AccountQualityResult({
  result, adding, onAddAccount, onRetry, onRerun, addError, addLabel,
  onForceAdd, forceLabel,
}) {
  if (!result) return null;
  const { status } = result;
  const profile = result.profile;
  const checks = result.checks || [];
  const ai = result.ai_assessment || {};
  const reasons = result.rejection_reasons || [];
  const actionRequired = result.action_required;
  const needsPublicView = actionRequired?.code === 'enable_public_profile';
  const checking = status === 'checking';
  const showReasons = !checking && reasons.length > 0 && !needsPublicView;

  return (
    <div className="rh-qg">
      <div className="rh-qg-header">
        <span className="rh-qg-title">Account quality check — u/{result.username}</span>
        {checking ? (
          <span className="rh-qg-checking"><span className="rh-qg-spinner" /> Checking…</span>
        ) : (
          <StatusBadge status={status} />
        )}
      </div>

      {checking && (
        <p style={{ fontSize: 12.5, color: 'var(--rh-text-muted)', margin: 0 }}>
          Inspecting the profile, recent comments and submissions. This usually takes 20–30 seconds.
        </p>
      )}

      {!checking && profile && (
        <div className="rh-qg-metrics">
          <div className="rh-qg-metric"><span>Account age</span><span>{profile.account_age_days != null ? `${profile.account_age_days} days` : '—'}</span></div>
          <div className="rh-qg-metric"><span>Comment karma</span><span>{profile.comment_karma != null ? profile.comment_karma.toLocaleString() : '—'}</span></div>
          <div className="rh-qg-metric"><span>Link karma</span><span>{profile.link_karma != null ? profile.link_karma.toLocaleString() : '—'}</span></div>
          <div className="rh-qg-metric"><span>Latest activity</span><span>{formatLatest(profile.latest_activity_at)}</span></div>
        </div>
      )}

      {!checking && <VisibilityActionCallout actionRequired={actionRequired} />}

      {!checking && checks.length > 0 && (
        <div className="rh-qg-checklist">
          {checks.map((c) => (
            <div className="rh-qg-check" key={c.key}>
              <span className={`rh-qg-check-icon ${c.status}`}>{CHECK_ICON[c.status] || '•'}</span>
              <span>
                <span className="rh-qg-check-key">{formatCheckKey(c.key)}: </span>
                {c.message}
              </span>
            </div>
          ))}
        </div>
      )}

      {!checking && (ai.summary || ai.risk_level) && (
        <div className="rh-qg-ai">
          <div className="rh-qg-ai-label">
            AI assessment{ai.risk_level ? ` — ${ai.risk_level} risk` : ''}
          </div>
          {ai.summary && <div>{ai.summary}</div>}
        </div>
      )}

      {!checking && result.niches && result.niches.length > 0 && (
        <div className="rh-qg-niche">
          <span className="rh-qg-niche-label">Niches</span>
          <NicheTags niches={result.niches} />
        </div>
      )}

      {showReasons && (
        <ul className="rh-qg-reasons">
          {reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}

      {!checking && status === 'error' && result.error_detail && (
        <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', marginTop: 0 }}>{result.error_detail}</p>
      )}

      {addError && <div className="rh-auth-error" style={{ marginTop: 8 }}>{addError}</div>}

      {!checking && (
        <div className="rh-qg-actions">
          {status === 'passed' && onAddAccount && (
            <button className="rh-btn-primary" onClick={onAddAccount} disabled={adding}>
              {adding ? 'Adding…' : (addLabel || 'Add Account')}
            </button>
          )}
          {needsPublicView && onRerun && (
            <button className="rh-btn-primary" onClick={onRerun} disabled={adding}>
              Re-run check
            </button>
          )}
          {(status === 'failed' || status === 'needs_manual_review') && onForceAdd && (
            <button className="rh-btn-force" onClick={onForceAdd} disabled={adding}>
              {adding ? 'Adding…' : (forceLabel || 'Force Add Anyway')}
            </button>
          )}
          {status === 'error' && onRetry && (
            <button className="rh-btn-secondary" onClick={onRetry} disabled={adding}>
              Retry
            </button>
          )}
          {(status === 'failed' || status === 'needs_manual_review') && onRetry && (
            <button className="rh-btn-secondary" onClick={onRetry} disabled={adding}>
              {needsPublicView ? 'Use a different username' : 'Check another username'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
